import React, { useState, useEffect, useCallback } from "react";
import './home.css';
import { MenuLateral } from "../Components/MenuPrincipal/MenuLateral";
import { useAuth } from "../Context/auth";
import Swal from 'sweetalert2';

// Importações do Chart.js
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

// Registro dos componentes do Chart.js (necessário para v3+)
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// Função auxiliar para formatar moeda
const formatarMoeda = (valor) => {
    return (Number(valor) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

function Home() {
    const { currentUser, appUserData } = useAuth(); // Pegamos currentUser para o token
    const [selectedPeriod, setSelectedPeriod] = useState('thisMonth'); // Default to this month
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Função para buscar os dados do dashboard na API
    const fetchDashboardData = useCallback(async () => {
        if (!currentUser || !appUserData || !appUserData['ler_dashboard']) return;
        setLoading(true);
        setError('');
        try {
            const token = await currentUser.getIdToken();
            const API_URL = process.env.REACT_APP_API_URL;

            // !! IMPORTANTE: '/api/dashboard' é a NOVA ROTA que você precisará criar no backend !!
            const sessionId = localStorage.getItem('sessionId'); // <-- Recupera a sessão
            const response = await fetch(`${API_URL}/api/dashboard?period=${selectedPeriod}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-session-id': sessionId // <-- Envia o header obrigatório
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Falha ao buscar dados do dashboard.');
            }

            const data = await response.json();
            setDashboardData(data);

        } catch (err) {
            console.error("Erro ao buscar dados do dashboard:", err);
            setError(err.message);
            setDashboardData(null); // Limpa dados em caso de erro
            Swal.fire('Erro', err.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [currentUser, appUserData, selectedPeriod]); // Depende do usuário e do período selecionado

    // Busca os dados quando o componente monta ou o período muda
    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]); // fetchDashboardData já inclui currentUser e selectedPeriod

    // ----- Configurações dos Gráficos -----

    const dailySalesChartData = {
        labels: dashboardData?.dailySales?.map(d => new Date(d.data).toLocaleDateString('pt-BR')) || [],
        datasets: [
            {
                label: 'Vendas Diárias',
                data: dashboardData?.dailySales?.map(d => d.valor_total) || [],
                borderColor: 'rgb(54, 162, 235)',
                backgroundColor: 'rgba(54, 162, 235, 0.5)',
                tension: 0.1
            },
        ],
    };

    const topCustomersChartData = {
        labels: dashboardData?.topCustomers?.map(c => c.cliente_nome) || [],
        datasets: [
            {
                label: 'Valor Comprado',
                data: dashboardData?.topCustomers?.map(c => c.valor_total_comprado) || [],
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1,
            },
        ],
    };

    const chartOptions = { // Opções genéricas podem ser aplicadas a ambos
        responsive: true,
        maintainAspectRatio: false, // Permite controlar a altura pelo CSS/container
        plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    // Formata o valor no tooltip como moeda
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += formatarMoeda(context.parsed.y);
                        }
                        return label;
                    }
                }
            }
        },
        // Garante que o eixo Y comece em 0
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    // Formata os ticks do eixo Y como moeda (opcional)
                    callback: function (value, index, values) {
                        return formatarMoeda(value);
                    }
                }
            }
        }
    };

    const topCustomersOptions = {
        ...chartOptions,
        indexAxis: 'y', // Faz o gráfico de barras ficar horizontal para melhor leitura dos nomes
        plugins: { // Sobrescreve tooltip se necessário
            legend: { display: false }, // Legenda não faz muito sentido aqui
            tooltip: { /* ... callbacks específicos se precisar ... */ }
        },
        scales: { // Eixo X formatado como moeda
            x: {
                beginAtZero: true,
                ticks: {
                    callback: function (value, index, values) {
                        return formatarMoeda(value);
                    }
                }
            },
            y: { // Garante que o eixo Y (nomes) não seja cortado
                ticks: {
                    autoSkip: false
                }
            }
        }
    };


    // ----- Renderização -----

    return (
        <div style={{ display: 'flex' }}>
            {/* <MenuPrincipal /> */}
            <MenuLateral />
            {/* Ajuste de main-content para o MenuLateral: margin-left 260px e removendo o padding do body via style inline (gambiarra de protótipo) ou container */}
            <div className="container-fluid content-with-sidebar home-dashboard">
                {/* Boas-vindas */}
                {appUserData && (
                    <h2 className="mb-3">Bem-vindo(a), {appUserData.user_nome}!</h2>
                )}

                {/* Conteúdo do Dashboard (apenas se tiver permissão) */}
                {appUserData && appUserData['ler_dashboard'] ? (
                    <>
                        {/* Seletor de Período */}
                        <div className="mb-4 d-flex justify-content-end align-items-center me-3">
                            <label className="me-2 fw-bold text-secondary">Período:</label>
                            <div style={{ width: '200px' }}>
                                <select
                                    className="form-select"
                                    value={selectedPeriod}
                                    onChange={(e) => setSelectedPeriod(e.target.value)}
                                >
                                    <option value="hoje">Hoje</option>
                                    <option value="thisWeek">Esta Semana</option>
                                    <option value="thisMonth">Este Mês</option>
                                    <option value="thisYear">Este Ano</option>
                                </select>
                            </div>
                        </div>

                        {/* Exibição de Loading ou Erro */}
                        {loading && (
                            <div className="text-center">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Carregando dados...</span>
                                </div>
                                <p>Carregando dados do dashboard...</p>
                            </div>
                        )}
                        {error && <div className="alert alert-danger">Erro ao carregar dados: {error}</div>}

                        {/* Conteúdo do Dashboard (KPIs e Gráficos) */}
                        {!loading && !error && dashboardData && (
                            <>
                                {/* KPIs */}
                                <div className="row g-3 mb-4">
                                    <div className="col-md-4">
                                        <div className="card text-center h-100 shadow border-left-primary">
                                            <div className="card-body">
                                                <h5 className="card-title">Quantidade de Pedidos</h5>
                                                <p className="card-text display-6">{dashboardData.kpis?.totalPedidos || 0}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="card text-center h-100 shadow border-left-success">
                                            <div className="card-body">
                                                <h5 className="card-title">Valor Vendido</h5>
                                                <p className="card-text display-6">{formatarMoeda(dashboardData.kpis?.totalVendido)}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="card text-center h-100 shadow border-left-info">
                                            <div className="card-body">
                                                <h5 className="card-title">Lucro Líquido</h5>
                                                <p className="card-text display-6">{formatarMoeda(dashboardData.kpis?.lucroLiquido)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Gráficos */}
                                <div className="row g-3">
                                    {/* Gráfico de Vendas Diárias */}
                                    <div className="col-lg-7">
                                        <div className="card h-100 shadow">
                                            <div className="card-body chart-container">
                                                <h5 className="card-title mb-3">Vendas Diárias</h5>
                                                {dashboardData.dailySales && dashboardData.dailySales.length > 0 ? (
                                                    <div style={{ position: 'relative', height: '300px' }}> {/* <-- Contêiner adicionado */}
                                                        <Line options={chartOptions} data={dailySalesChartData} />
                                                    </div>
                                                ) : (
                                                    <p className="text-center text-muted">Sem dados de vendas para o período.</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Gráfico de Top Clientes */}
                                    <div className="col-lg-5">
                                        <div className="card h-100 shadow">
                                            <div className="card-body chart-container">
                                                <h5 className="card-title mb-3">Top 5 Clientes (por Valor)</h5>
                                                {dashboardData.topCustomers && dashboardData.topCustomers.length > 0 ? (
                                                    <div style={{ position: 'relative', height: '300px' }}> {/* <-- Contêiner adicionado */}
                                                        <Bar options={topCustomersOptions} data={topCustomersChartData} />
                                                    </div>
                                                ) : (
                                                    <p className="text-center text-muted">Sem dados de clientes para o período.</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                ) : null}
            </div>
        </div>
    );
}

export default Home;